/**
 * ReservationFiltersHeader - Top-level filtering controls for reservations
 * Provides search, service/customer filters, and status buttons
 */

import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, CalendarDaysIcon, UserIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ReservationFiltersHeader = ({
  filters,
  onFiltersChange,
  showSearchReservations = false,
  showServiceFilter = false,
  showCustomerFilter = true,
  reservationCounts = {},
  services = [],
  customers = [],
  reservationType = 'items' // 'items' or 'services'
}) => {

  // Rental notification settings (for items tab only)
  const [rentalSettings, setRentalSettings] = useState({
    maxRentalDurationHours: 0,
    notificationFlagMinutes: 0
  });

  // Fetch rental notification settings
  useEffect(() => {
    const fetchRentalSettings = async () => {
      if (reservationType !== 'items') return;

      try {
        const token = localStorage.getItem('token'); // Get token from localStorage
        const response = await axios.get('/api/settings/organization/general', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data) {
          setRentalSettings({
            maxRentalDurationHours: response.data.maxRentalDurationHours || 0,
            notificationFlagMinutes: response.data.notificationFlagMinutes || 0
          });
        }
      } catch (error) {
        console.error('Error fetching rental notification settings:', error);
      }
    };

    fetchRentalSettings();
  }, [reservationType]);

  const handleFilterChange = (key, value) => {
    console.log('🔧 Filter Change:', { key, value, currentFilters: filters });

    if (key === 'serviceFilter') {
      console.log('🔧 Service Filter Selected:', {
        selectedValue: value,
        availableServices: services,
        servicesCount: services.length
      });
    }

    const newFilters = { ...filters, [key]: value };
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    onFiltersChange({
      status: 'all',
      rentalStatus: 'all',
      searchTerm: '',
      serviceFilter: '',
      customerFilter: ''
    });
  };

  const hasActiveFilters = () => {
    return (
      filters.status !== 'all' ||
      (filters.rentalStatus && filters.rentalStatus !== 'all') ||
      filters.searchTerm ||
      filters.serviceFilter ||
      filters.customerFilter
    );
  };

  // Different status options based on reservation type
  const getStatusOptions = () => {
    if (reservationType === 'services') {
      return [
        { key: 'all', label: 'All', count: reservationCounts.all || 0 },
        { key: 'CONFIRMED', label: 'Confirmed', count: reservationCounts.confirmed || 0 },
        { key: 'PENDING_PAYMENT', label: 'Pending Payment', count: reservationCounts.pendingPayment || 0 },
        { key: 'COMPLETED', label: 'Completed', count: reservationCounts.completed || 0 },
        { key: 'CANCELLED', label: 'Cancelled', count: reservationCounts.cancelled || 0 }
      ];
    } else {
      // Default for items/regular reservations
      return [
        { key: 'all', label: 'All', count: reservationCounts.all || 0 },
        { key: 'ACTIVE', label: 'Active', count: reservationCounts.active || 0 },
        { key: 'COMPLETED', label: 'Completed', count: reservationCounts.completed || 0 },
        { key: 'CANCELLED', label: 'Cancelled', count: reservationCounts.cancelled || 0 }
      ];
    }
  };

  const statusOptions = getStatusOptions();

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Filter Inputs Row */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          {/* Search Reservations - Only on reservations tab */}
          {showSearchReservations && (
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search reservations..."
                  value={filters.searchTerm || ''}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>
          )}

          {/* Filter by Service - Only on services tab */}
          {showServiceFilter && (
            <div className="flex-1">
              <div className="relative">
                <select
                  value={filters.serviceFilter || ''}
                  onChange={(e) => handleFilterChange('serviceFilter', e.target.value)}
                  className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Filter by service...</option>
                  {Array.isArray(services) && services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
                <CalendarDaysIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                <svg className="w-5 h-5 text-gray-400 absolute right-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}

          {/* Filter by Customer - On both tabs */}
          {showCustomerFilter && (
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Filter by customer..."
                  value={filters.customerFilter || ''}
                  onChange={(e) => handleFilterChange('customerFilter', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <UserIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>
          )}
        </div>

        {/* Status Filter Buttons Row */}
        <div className="flex items-center justify-between">
          <div className="overflow-x-auto overflow-y-hidden scrollbar-hide" style={{ overscrollBehaviorX: 'contain', overscrollBehaviorY: 'none' }}>
            <div className="flex items-center space-x-1 min-w-max">
            {statusOptions.map((option) => (
              <button
                key={option.key}
                onClick={() => handleFilterChange('status', option.key)}
                className={`
                  inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${filters.status === option.key
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
              >
                {option.label}
                <span className={`
                  ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium
                  ${filters.status === option.key
                    ? 'bg-blue-200 text-blue-800'
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {option.count}
                </span>
              </button>
            ))}

            {/* Rental Status Filters - Only for items tab */}
            {reservationType === 'items' && rentalSettings.maxRentalDurationHours > 0 && (
              <>
                <div className="w-px h-6 bg-gray-300 mx-2"></div>
                <button
                  onClick={() => handleFilterChange('rentalStatus', 'WARNING')}
                  className={`
                    inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${filters.rentalStatus === 'WARNING'
                      ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-yellow-50'
                    }
                  `}
                >
                  ⚠️ Approaching Limit
                </button>
                <button
                  onClick={() => handleFilterChange('rentalStatus', 'OVERDUE')}
                  className={`
                    inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${filters.rentalStatus === 'OVERDUE'
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-red-50'
                    }
                  `}
                >
                  🔴 Overdue
                </button>
              </>
            )}
            </div>
          </div>

          {/* Clear All Filters */}
          {hasActiveFilters() && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg font-medium transition-colors"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationFiltersHeader;